import {
    ConditionalFormattingColorApplyTo,
    formatItemValue,
    getConditionalFormattingColor,
    getConditionalFormattingConfig,
    getConditionalFormattingDescription,
    getItemId,
    isDimension,
    isField,
    isHexCodeColor,
    isNumericItem,
    isSummable,
    type ColumnProperties,
    type ConditionalFormattingConfig,
    type ConditionalFormattingMinMaxMap,
    type ConditionalFormattingRowFields,
    type ItemsMap,
    type PivotData,
    type ResultRow,
    type ResultValue,
} from '@lightdash/common';
import {
    Button,
    Group,
    Text,
    useMantineColorScheme,
    type BoxProps,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
    type GroupingState,
    type Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import { readableColor } from 'polished';
import React, { useCallback, useEffect, useMemo, useRef, type FC } from 'react';
import {
    getGroupingValuesAndSubtotalKey,
    getSubtotalValueFromGroup,
} from '../../../hooks/tableVisualization/getDataAndColumns';
import {
    formatCellContent,
    getFormattedValueCell,
} from '../../../hooks/useColumns';
import {
    getColorFromRange,
    transformColorsForDarkMode,
} from '../../../utils/colorUtils';
import { getConditionalRuleLabelFromItem } from '../Filters/FilterInputs/utils';
import Table from '../LightTable';
import { CELL_HEIGHT } from '../LightTable/constants';
import MantineIcon from '../MantineIcon';
import { ROW_NUMBER_COLUMN_ID } from '../Table/constants';
import { getGroupedRowModelLightdash } from '../Table/getGroupedRowModelLightdash';
import { columnHelper, type TableColumn } from '../Table/types';
import { useColumnResize } from '../Table/useColumnResize';
import { countSubRows } from '../Table/utils';
import pivotStyles from './PivotTable.module.css';
import TotalCellMenu from './TotalCellMenu';
import ValueCellMenu from './ValueCellMenu';

const ROW_NUMBER_COL_WIDTH = 50;
const MIN_AUTO_COL_WIDTH = 50;

type MenuCallbackProps = {
    isOpen: boolean;
    onClose: () => void;
    onCopy: () => void;
};

type RenderCallback = () => React.ReactNode;

const rowColumn: TableColumn = {
    id: ROW_NUMBER_COLUMN_ID,
    cell: (props) => props.row.index + 1,
    enableGrouping: false,
};

const allPivotedSpacerColumn: TableColumn = {
    id: 'all-pivoted-spacer',
    cell: () => null,
    enableGrouping: false,
};

const VirtualizedArea: FC<{
    cellCount: number;
    height: number;
}> = ({ cellCount, height }) => {
    return (
        <Table.Row index={-1}>
            {[...Array(cellCount)].map((_, index) => (
                <Table.Cell key={index} h={height} />
            ))}
        </Table.Row>
    );
};

type IndexDimension = { fieldId: string; value: unknown };

const extractIndexDimensions = (
    indexValueRow: PivotData['indexValues'][number],
): IndexDimension[] =>
    indexValueRow
        .filter((v) => v.type === 'value')
        .map((v) => ({
            fieldId: v.fieldId,
            value: 'value' in v ? v.value?.raw : undefined,
        }));

type PivotTableProps = BoxProps & // TODO: remove this
    React.RefAttributes<HTMLTableElement> & {
        data: PivotData;
        conditionalFormattings: ConditionalFormattingConfig[];
        minMaxMap: ConditionalFormattingMinMaxMap | undefined;
        hideRowNumbers: boolean;
        getFieldLabel: (fieldId: string) => string | undefined;
        getField: (fieldId: string) => ItemsMap[string] | undefined;
        showSubtotals?: boolean;
        columnProperties?: Record<string, ColumnProperties>;
        isMinimal: boolean;
        isDashboard?: boolean;
        onColumnWidthChange?: (fieldId: string, width: number) => void;
    };

const PivotTable: FC<PivotTableProps> = ({
    data,
    conditionalFormattings,
    minMaxMap = {},
    hideRowNumbers = false,
    getFieldLabel,
    getField,
    className,
    showSubtotals = false,
    columnProperties = {},
    isMinimal = false,
    isDashboard = false,
    onColumnWidthChange,
    ...tableProps
}) => {
    const { colorScheme } = useMantineColorScheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [grouping, setGrouping] = React.useState<GroupingState>([]);

    const { handleResizeStart, resizeHandleClassName } = useColumnResize({
        onColumnWidthChange,
    });

    // Count label/index columns to compute offset from headerColIndex to pivotColumnInfo
    const numLabelCols = useMemo(
        () =>
            data.retrofitData.pivotColumnInfo.filter(
                (col) =>
                    col.columnType === 'label' ||
                    col.columnType === 'indexValue',
            ).length,
        [data],
    );

    const hasColumnTotals = data.pivotConfig.columnTotals;

    const hasRowTotals = data.pivotConfig.rowTotals;

    const hasCustomWidths = useMemo(
        () =>
            Object.values(columnProperties).some(
                (prop) => prop?.width !== undefined,
            ),
        [columnProperties],
    );

    const { columns, columnOrder, colWidths } = useMemo(() => {
        // Pivoting all dimensions requires a spacer column under the pivoted headers.
        const allDimensionsPivoted =
            data.indexValueTypes.length === 0 &&
            data.titleFields[0].length === 1;

        const indexPlaceholders: Record<string, ResultValue>[] = Array(
            data.indexValueTypes.length,
        ).fill({});

        const headerPlaceholders: Record<string, ResultValue>[] = Array(
            data.headerValues.length,
        ).fill({});

        const headerInfoForColumns = data.headerValues.reduce<
            Array<Record<string, ResultValue>>
        >((acc, headerRow) => {
            return headerRow.map((headerColValue, headerColIndex) =>
                'value' in headerColValue
                    ? {
                          ...acc[headerColIndex],
                          [headerColValue.fieldId]: headerColValue.value,
                      }
                    : acc[headerColIndex],
            );
        }, headerPlaceholders);

        const finalHeaderInfoForColumns = [
            ...indexPlaceholders,
            ...headerInfoForColumns,
        ];

        const newColumnOrder: string[] = [];
        const newColWidths: (number | undefined)[] = [];
        if (!hideRowNumbers) {
            newColumnOrder.push(ROW_NUMBER_COLUMN_ID);
            newColWidths.push(ROW_NUMBER_COL_WIDTH);
        }
        if (allDimensionsPivoted) {
            newColumnOrder.push(allPivotedSpacerColumn.id);
            newColWidths.push(undefined);
        }

        let newColumns = data.retrofitData.pivotColumnInfo.map(
            (col, colIndex) => {
                newColumnOrder.push(col.fieldId);

                const itemId = col.underlyingId || col.baseId || col.fieldId;
                const item = itemId ? getField(itemId) : undefined;
                // For indexValue columns, use fieldId (actual dimension ID).
                // For data columns, use underlyingId (metric) or baseId (last pivot dim).
                const widthKey =
                    col.columnType === 'indexValue' ||
                    col.columnType === 'label'
                        ? col.fieldId
                        : col.underlyingId || col.baseId;
                const colWidth = widthKey
                    ? columnProperties[widthKey]?.width
                    : undefined;
                newColWidths.push(colWidth);
                const column: TableColumn = columnHelper.accessor(
                    (row: ResultRow) => {
                        return row[col.fieldId];
                    },
                    {
                        id: col.fieldId,
                        cell: getFormattedValueCell,
                        meta: {
                            item: item,
                            width: colWidth,
                            type: col.columnType,
                            headerInfo:
                                colIndex < finalHeaderInfoForColumns.length
                                    ? finalHeaderInfoForColumns[colIndex]
                                    : undefined,
                        },
                        aggregatedCell: (info) => {
                            if (info.row.getIsGrouped()) {
                                const groupingValuesAndSubtotalKey =
                                    getGroupingValuesAndSubtotalKey(info);

                                if (!groupingValuesAndSubtotalKey) {
                                    return null;
                                }

                                const { groupingValues, subtotalGroupKey } =
                                    groupingValuesAndSubtotalKey;

                                // Get the pivoted header values for the column
                                const pivotedHeaderValues =
                                    finalHeaderInfoForColumns[colIndex];

                                // Find the subtotal for the row, this is used to find the subtotal in the groupedSubtotals object
                                const subtotal = data.groupedSubtotals?.[
                                    subtotalGroupKey
                                ]?.find((sub) => {
                                    try {
                                        return (
                                            // All grouping values in the row must match the subtotal values
                                            Object.keys(groupingValues).every(
                                                (key) => {
                                                    return (
                                                        groupingValues[key]
                                                            ?.value.raw ===
                                                        sub[key]
                                                    );
                                                },
                                            ) &&
                                            // All pivoted header values in the row must match the subtotal values
                                            Object.keys(
                                                pivotedHeaderValues,
                                            ).every((key) => {
                                                return (
                                                    pivotedHeaderValues[key]
                                                        ?.raw === sub[key]
                                                );
                                            })
                                        );
                                    } catch (e) {
                                        console.error(e);
                                        return false;
                                    }
                                });

                                const subtotalValue = getSubtotalValueFromGroup(
                                    subtotal,
                                    col.baseId ?? col.fieldId,
                                );

                                if (subtotalValue === null) {
                                    return null;
                                }

                                return (
                                    <Text span fw={600}>
                                        {formatItemValue(item, subtotalValue)}
                                    </Text>
                                );
                            }
                        },
                    },
                );
                return column;
            },
        );

        if (allDimensionsPivoted)
            newColumns = [allPivotedSpacerColumn, ...newColumns];
        if (!hideRowNumbers) newColumns = [rowColumn, ...newColumns];

        return {
            columns: newColumns,
            columnOrder: newColumnOrder,
            colWidths: newColWidths,
        };
    }, [data, hideRowNumbers, getField, columnProperties]);

    // Minimum table width so auto columns don't get squeezed to zero
    const minTableWidth = useMemo(() => {
        if (!hasCustomWidths) return undefined;
        return colWidths.reduce<number>(
            (sum, w) => sum + (w ?? MIN_AUTO_COL_WIDTH),
            0,
        );
    }, [hasCustomWidths, colWidths]);

    const table = useReactTable({
        data: data.retrofitData.allCombinedData,
        columns: columns,
        state: {
            grouping,
            columnOrder: columnOrder,
            columnPinning: {
                left: [ROW_NUMBER_COLUMN_ID],
            },
        },
        onGroupingChange: setGrouping,
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModelLightdash(),
        getCoreRowModel: getCoreRowModel(),
        meta: {
            columnProperties,
            minMaxMap,
        },
    });

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
        getScrollElement: () => containerRef.current,
        count: rows.length,
        estimateSize: () => CELL_HEIGHT,
        overscan: 25,
    });
    const virtualRows = rowVirtualizer.getVirtualItems();

    const getColumnTotalValueFromAxis = useCallback(
        (total: unknown, colIndex: number): ResultValue | null => {
            const value = last(data.headerValues)?.[colIndex];
            if (!value || !value.fieldId) throw new Error('Invalid pivot data');

            const item = getField(value.fieldId);
            if (!isSummable(item)) {
                return null;
            }
            const formattedValue = formatItemValue(item, total);

            return {
                raw: total,
                formatted: formattedValue,
            };
        },
        [data.headerValues, getField],
    );

    const getMetricAsRowColumnTotalValueFromAxis = useCallback(
        (total: unknown, rowIndex: number): ResultValue => {
            const value = last(data.columnTotalFields?.[rowIndex]);
            if (!value || !value.fieldId) throw new Error('Invalid pivot data');

            const item = getField(value.fieldId);

            const formattedValue = formatItemValue(item, total);

            return {
                raw: total,
                formatted: formattedValue,
            };
        },
        [data.columnTotalFields, getField],
    );

    const getUnderlyingFieldValues = useCallback(
        (rowIndex: number, colIndex: number) => {
            const visibleCells = rows[rowIndex].getVisibleCells();
            const visibleCell = visibleCells[colIndex];
            const item = visibleCell.column.columnDef.meta?.item;
            const fullItemValue = visibleCell.getValue() as ResultRow[0];
            const itemValue = fullItemValue.value;
            let underlyingValues =
                isField(item) && itemValue
                    ? { [getItemId(item)]: itemValue }
                    : {};
            visibleCells.forEach((cell, cellIndex) => {
                if (cell.column.columnDef.meta?.type === 'indexValue') {
                    if (cell.column.columnDef.id) {
                        const fullValue = cell.getValue() as
                            | ResultRow[0]
                            | undefined;

                        if (fullValue) {
                            underlyingValues[cell.column.columnDef.id] =
                                fullValue.value;
                        }
                    }
                } else if (cell.column.columnDef.meta?.type === 'label') {
                    const info = data.indexValues[rowIndex].find(
                        (indexValue) => indexValue.type === 'label',
                    );
                    if (info) underlyingValues[info.fieldId] = itemValue;
                } else if (
                    colIndex === cellIndex &&
                    cell.column.columnDef.meta?.headerInfo
                ) {
                    underlyingValues = {
                        ...underlyingValues,
                        ...cell.column.columnDef.meta.headerInfo,
                    };
                }
            });
            return underlyingValues;
        },
        [rows, data.indexValues],
    );

    // Find the data column index from headerInfo by matching against headerValues
    // Used for metricsAsRows mode to look up values from dataValues
    const findDataColumnIndex = useCallback(
        (
            headerInfo: Record<string, ResultValue> | undefined,
        ): number | undefined => {
            if (!headerInfo || data.headerValues.length === 0) return undefined;

            const numColumns = data.headerValues[0]?.length ?? 0;

            // Find column where ALL header dimension values match
            for (let colIdx = 0; colIdx < numColumns; colIdx++) {
                const allMatch = data.headerValues.every((headerRow) => {
                    const headerCell = headerRow[colIdx];
                    if (headerCell?.type !== 'value') return true;
                    const expectedValue = headerInfo[headerCell.fieldId]?.raw;
                    return headerCell.value?.raw === expectedValue;
                });
                if (allMatch) return colIdx;
            }
            return undefined;
        },
        [data.headerValues],
    );

    // Build rowFields entries from headerInfo (pivoted dimension values)
    // This ensures conditional formatting rules that reference pivoted dimensions work correctly
    const buildRowFieldsFromHeaderInfo = useCallback(
        (
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            if (!headerInfo) return {};

            return Object.entries(
                headerInfo,
            ).reduce<ConditionalFormattingRowFields>(
                (acc, [fieldId, resultValue]) => {
                    const field = getField(fieldId);
                    if (field && isDimension(field)) {
                        acc[fieldId] = {
                            field,
                            value: resultValue?.raw,
                        };
                    }
                    return acc;
                },
                {},
            );
        },
        [getField],
    );

    // Build rowFields for metricsAsRows mode by looking up metric values across rows
    // that share the same index dimension values (same "row group" in the original data)
    const buildRowFieldsForMetricsAsRows = useCallback(
        (
            rowIndex: number,
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            const dataColIndex = findDataColumnIndex(headerInfo);
            if (dataColIndex === undefined) return {};

            // Include pivoted dimension values from headerInfo
            const pivotedDimensionFields =
                buildRowFieldsFromHeaderInfo(headerInfo);

            const currentIndexDims = extractIndexDimensions(
                data.indexValues[rowIndex] ?? [],
            );

            // Include row dimension values from indexValues
            const rowDimensionFields =
                currentIndexDims.reduce<ConditionalFormattingRowFields>(
                    (acc, dim) => {
                        const field = getField(dim.fieldId);
                        if (field && isDimension(field)) {
                            acc[dim.fieldId] = { field, value: dim.value };
                        }
                        return acc;
                    },
                    {},
                );

            const metricFields =
                data.indexValues.reduce<ConditionalFormattingRowFields>(
                    (acc, indexValueRow, metricRowIdx) => {
                        const rowIndexDims =
                            extractIndexDimensions(indexValueRow);

                        // Only include metrics from same index dimension group
                        const sameIndexGroup =
                            currentIndexDims.length === rowIndexDims.length &&
                            currentIndexDims.every(
                                (dim, i) =>
                                    rowIndexDims[i]?.fieldId === dim.fieldId &&
                                    rowIndexDims[i]?.value === dim.value,
                            );
                        if (!sameIndexGroup) return acc;

                        const labelEntry = indexValueRow.find(
                            (v) => v.type === 'label',
                        );
                        if (!labelEntry) return acc;

                        const metricId = labelEntry.fieldId;
                        const field = getField(metricId);
                        const metricValue =
                            data.dataValues[metricRowIdx]?.[dataColIndex];

                        if (field && !isDimension(field)) {
                            acc[metricId] = { field, value: metricValue?.raw };
                        }
                        return acc;
                    },
                    {},
                );

            // Merge pivoted dimensions, row dimensions, and metrics
            return {
                ...pivotedDimensionFields,
                ...rowDimensionFields,
                ...metricFields,
            };
        },
        [
            data.indexValues,
            data.dataValues,
            findDataColumnIndex,
            getField,
            buildRowFieldsFromHeaderInfo,
        ],
    );

    // Build rowFields for normal pivot mode from visible cells with matching header context
    const buildRowFieldsFromVisibleCells = useCallback(
        (
            row: Row<ResultRow>,
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            // Include pivoted dimension values from headerInfo
            const pivotedDimensionFields =
                buildRowFieldsFromHeaderInfo(headerInfo);

            const cellFields = row
                .getVisibleCells()
                .reduce<ConditionalFormattingRowFields>((acc, c) => {
                    const cellMeta = c.column.columnDef.meta;
                    if (!cellMeta?.item) return acc;

                    // Include index (row) dimensions regardless of headerInfo match
                    // Include data cells only if they match the current pivot column context
                    const isIndexDimension = cellMeta.type === 'indexValue';
                    const matchesHeaderContext = isEqual(
                        cellMeta.headerInfo,
                        headerInfo,
                    );

                    if (isIndexDimension || matchesHeaderContext) {
                        const cellValue = c.getValue() as
                            | ResultRow[0]
                            | undefined;
                        acc[getItemId(cellMeta.item)] = {
                            field: cellMeta.item,
                            value: cellValue?.value?.raw,
                        };
                    }
                    return acc;
                }, {});

            // Merge pivoted dimensions with cell fields
            return { ...pivotedDimensionFields, ...cellFields };
        },
        [buildRowFieldsFromHeaderInfo],
    );

    const paddingTop = useMemo(() => {
        return virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    }, [virtualRows]);

    const paddingBottom = useMemo(() => {
        return virtualRows.length > 0
            ? rowVirtualizer.getTotalSize() -
                  (virtualRows?.[virtualRows.length - 1]?.end || 0)
            : 0;
    }, [virtualRows, rowVirtualizer]);

    const cellsCountWithRowNumber = useMemo(() => {
        return (hideRowNumbers ? 0 : 1) + data.cellsCount;
    }, [hideRowNumbers, data.cellsCount]);

    useEffect(() => {
        // TODO: Remove code duplicated from non-pivot table version.
        if (showSubtotals) {
            const groupedColumns = data.indexValueTypes.map(
                (valueType) => valueType.fieldId,
            );

            const sortedColumns = table
                .getState()
                .columnOrder.reduce<string[]>((acc, sortedId) => {
                    return groupedColumns.includes(sortedId)
                        ? [...acc, sortedId]
                        : acc;
                }, [])
                // The last dimension column essentially groups rows for each unique value in that column.
                // Grouping on it would result in many useless expandable groups containing just one item.
                .slice(0, -1);

            if (!isEqual(sortedColumns, table.getState().grouping)) {
                table.setGrouping(sortedColumns);
            }
        } else {
            if (table.getState().grouping.length > 0) {
                table.resetGrouping();
            }
        }
    }, [showSubtotals, data.indexValueTypes, table, columnOrder]);

    return (
        <Table
            miw={minTableWidth ? `max(100%, ${minTableWidth}px)` : '100%'}
            w={hasCustomWidths ? '100%' : undefined}
            className={
                hasCustomWidths
                    ? `${pivotStyles.fixedLayout}${className ? ` ${className}` : ''}`
                    : className
            }
            {...tableProps}
            containerRef={containerRef}
            isDashboard={isDashboard}
        >
            {hasCustomWidths && (
                <colgroup>
                    {colWidths.map((cw, i) => (
                        <col
                            key={i}
                            style={
                                cw !== undefined
                                    ? { width: `${cw}px` }
                                    : undefined
                            }
                        />
                    ))}
                </colgroup>
            )}
            <Table.Head withSticky>
                {data.headerValues.map((headerValues, headerRowIndex) => (
                    <Table.Row
                        key={`header-row-${headerRowIndex}`}
                        index={headerRowIndex}
                    >
                        {/* shows empty cell if row numbers are visible */}
                        {hideRowNumbers ? null : headerRowIndex <
                          data.headerValues.length - 1 ? (
                            <Table.Cell
                                isMinimal={isMinimal}
                                withMinimalWidth={!hasCustomWidths}
                            />
                        ) : (
                            <Table.CellHead
                                isMinimal={isMinimal}
                                withMinimalWidth={!hasCustomWidths}
                                withBoldFont
                            >
                                #
                            </Table.CellHead>
                        )}
                        {/* renders the title labels */}
                        {data.titleFields[headerRowIndex].map(
                            (titleField, titleFieldIndex) => {
                                const field = titleField?.fieldId
                                    ? getField(titleField?.fieldId)
                                    : undefined;

                                const isEmpty = !titleField?.fieldId;

                                const isHeaderTitle =
                                    titleField?.direction === 'header';

                                const isLastHeaderRow =
                                    headerRowIndex ===
                                    data.headerValues.length - 1;

                                const titleWidthKey = titleField?.fieldId;
                                const titleWidth = titleWidthKey
                                    ? columnProperties[titleWidthKey]?.width
                                    : undefined;
                                const canResizeTitle =
                                    isLastHeaderRow &&
                                    onColumnWidthChange &&
                                    !isMinimal &&
                                    titleWidthKey;

                                return isEmpty ? (
                                    <Table.Cell
                                        key={`title-${headerRowIndex}-${titleFieldIndex}`}
                                        isMinimal={isMinimal}
                                        withMinimalWidth={
                                            !hasCustomWidths && !titleWidth
                                        }
                                    />
                                ) : (
                                    <Table.CellHead
                                        key={`title-${headerRowIndex}-${titleFieldIndex}`}
                                        withAlignRight={isHeaderTitle}
                                        isMinimal={isMinimal}
                                        withMinimalWidth={
                                            !hasCustomWidths &&
                                            (!isLastHeaderRow || !titleWidth)
                                        }
                                        withBoldFont
                                        withTooltip={
                                            isField(field)
                                                ? field.description
                                                : undefined
                                        }
                                        w={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                        miw={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                        maw={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                    >
                                        {titleField?.fieldId
                                            ? getFieldLabel(titleField?.fieldId)
                                            : undefined}
                                        {canResizeTitle && (
                                            <div
                                                className={
                                                    resizeHandleClassName
                                                }
                                                role="separator"
                                                aria-orientation="vertical"
                                                onMouseDown={(e) =>
                                                    handleResizeStart(
                                                        e,
                                                        titleWidthKey,
                                                    )
                                                }
                                            />
                                        )}
                                    </Table.CellHead>
                                );
                            },
                        )}
                        {/* renders the header values or labels */}
                        {headerValues.map((headerValue, headerColIndex) => {
                            const isLabel = headerValue.type === 'label';
                            const field = getField(headerValue.fieldId);

                            const description =
                                isLabel && isField(field)
                                    ? field.description
                                    : undefined;

                            const isLastHeaderRow =
                                headerRowIndex === data.headerValues.length - 1;

                            // Look up saved width for this data column
                            const colInfo =
                                data.retrofitData.pivotColumnInfo[
                                    numLabelCols + headerColIndex
                                ];
                            const widthKey =
                                colInfo?.underlyingId || colInfo?.baseId;
                            const colWidth = widthKey
                                ? columnProperties[widthKey]?.width
                                : undefined;

                            const canResize =
                                isLastHeaderRow &&
                                onColumnWidthChange &&
                                !isMinimal &&
                                widthKey;

                            // Apply width on the last header row, or on parent rows
                            // when the cell spans exactly 1 column (single metric)
                            const effectiveWidth =
                                isLastHeaderRow ||
                                (!isLabel && headerValue.colSpan === 1)
                                    ? colWidth
                                    : undefined;

                            return isLabel || headerValue.colSpan > 0 ? (
                                <Table.CellHead
                                    key={`header-${headerRowIndex}-${headerColIndex}`}
                                    isMinimal={isMinimal}
                                    withBoldFont={isLabel}
                                    withTooltip={description}
                                    colSpan={
                                        isLabel
                                            ? undefined
                                            : headerValue.colSpan
                                    }
                                    w={effectiveWidth}
                                    miw={effectiveWidth}
                                    maw={effectiveWidth}
                                >
                                    {isLabel
                                        ? getFieldLabel(headerValue.fieldId)
                                        : formatCellContent(headerValue)}
                                    {canResize && (
                                        <div
                                            className={resizeHandleClassName}
                                            role="separator"
                                            aria-orientation="vertical"
                                            onMouseDown={(e) =>
                                                handleResizeStart(e, widthKey)
                                            }
                                        />
                                    )}
                                </Table.CellHead>
                            ) : null;
                        })}
                        {/* render the total label */}
                        {hasRowTotals
                            ? data.rowTotalFields?.[headerRowIndex].map(
                                  (totalLabel, headerColIndex) =>
                                      totalLabel ? (
                                          <Table.CellHead
                                              key={`header-total-${headerRowIndex}-${headerColIndex}`}
                                              isMinimal={isMinimal}
                                              withBoldFont
                                              withMinimalWidth={
                                                  !hasCustomWidths
                                              }
                                          >
                                              {totalLabel.fieldId
                                                  ? `Total ${getFieldLabel(
                                                        totalLabel.fieldId,
                                                    )}`
                                                  : `Total`}
                                          </Table.CellHead>
                                      ) : (
                                          <Table.Cell
                                              key={`header-total-${headerRowIndex}-${headerColIndex}`}
                                              isMinimal={isMinimal}
                                              withMinimalWidth={
                                                  !hasCustomWidths
                                              }
                                          />
                                      ),
                              )
                            : null}
                    </Table.Row>
                ))}
            </Table.Head>

            <Table.Body>
                {paddingTop > 0 && (
                    <VirtualizedArea
                        cellCount={cellsCountWithRowNumber}
                        height={paddingTop}
                    />
                )}

                {virtualRows.map((virtualRow) => {
                    const rowIndex = virtualRow.index;
                    const row = rows[rowIndex];
                    if (!row) return null;

                    const toggleExpander = row.getToggleExpandedHandler();

                    return (
                        <Table.Row
                            key={`row-${rowIndex}-${data.pivotConfig.metricsAsRows}`}
                            index={rowIndex}
                        >
                            {row.getVisibleCells().map((cell, colIndex) => {
                                const meta = cell.column.columnDef.meta;
                                const isRowTotal = meta?.type === 'rowTotal';
                                const isDataColumn =
                                    meta?.type !== 'indexValue' &&
                                    meta?.type !== 'label' &&
                                    !isRowTotal;
                                let item = meta?.item;

                                if (
                                    data.pivotConfig.metricsAsRows &&
                                    isDataColumn
                                ) {
                                    const metricLabelInfo = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    );
                                    if (metricLabelInfo) {
                                        item = getField(
                                            metricLabelInfo.fieldId,
                                        );
                                    }
                                } else if (item && isDimension(item)) {
                                    const underlyingId = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    )?.fieldId;
                                    item = underlyingId
                                        ? getField(underlyingId)
                                        : item;
                                }

                                const fullValue =
                                    cell.getValue() as ResultRow[0];
                                const value = fullValue?.value;

                                // Build rowFields for this cell's pivot context only
                                // This ensures field comparisons use values from the same pivot column
                                const currentHeaderInfo =
                                    cell.column.columnDef.meta?.headerInfo;

                                const rowFieldsForCell = data.pivotConfig
                                    .metricsAsRows
                                    ? buildRowFieldsForMetricsAsRows(
                                          rowIndex,
                                          currentHeaderInfo,
                                      )
                                    : buildRowFieldsFromVisibleCells(
                                          row,
                                          currentHeaderInfo,
                                      );

                                const conditionalFormattingConfig =
                                    getConditionalFormattingConfig({
                                        field: item,
                                        value: value?.raw,
                                        minMaxMap,
                                        conditionalFormattings,
                                        rowFields: rowFieldsForCell,
                                    });

                                const conditionalFormattingResult =
                                    getConditionalFormattingColor({
                                        field: item,
                                        value: value?.raw,
                                        config: conditionalFormattingConfig,
                                        minMaxMap,
                                        getColorFromRange: (
                                            val,
                                            colorRange,
                                            minMaxRange,
                                        ) => {
                                            const effectiveColorRange =
                                                colorScheme === 'dark'
                                                    ? transformColorsForDarkMode(
                                                          colorRange,
                                                      )
                                                    : colorRange;
                                            return getColorFromRange(
                                                val,
                                                effectiveColorRange,
                                                minMaxRange,
                                            );
                                        },
                                    });

                                const applyToText =
                                    conditionalFormattingResult?.applyTo ===
                                    ConditionalFormattingColorApplyTo.TEXT;

                                const conditionalFormatting = (() => {
                                    const tooltipContent =
                                        getConditionalFormattingDescription(
                                            item,
                                            conditionalFormattingConfig,
                                            rowFieldsForCell,
                                            getConditionalRuleLabelFromItem,
                                        );

                                    if (
                                        !conditionalFormattingResult ||
                                        !isHexCodeColor(
                                            conditionalFormattingResult.color,
                                        )
                                    ) {
                                        return undefined;
                                    }

                                    // When applying to text, set color directly without background
                                    // When applying to cell, set background and calculate readable text color
                                    return applyToText
                                        ? {
                                              tooltipContent,
                                              color: conditionalFormattingResult.color,
                                          }
                                        : {
                                              tooltipContent,
                                              color: readableColor(
                                                  conditionalFormattingResult.color,
                                              ),
                                              backgroundColor:
                                                  conditionalFormattingResult.color,
                                          };
                                })();

                                // Font color is set by conditionalFormatting above
                                const fontColor = conditionalFormatting?.color;

                                // Get field description for label cells (metric labels when metricsAsRows is enabled)
                                const labelFieldDescription = (() => {
                                    if (meta?.type !== 'label')
                                        return undefined;
                                    const labelInfo = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    );
                                    if (!labelInfo) return undefined;
                                    const labelField = getField(
                                        labelInfo.fieldId,
                                    );
                                    return isField(labelField)
                                        ? labelField.description
                                        : undefined;
                                })();

                                const suppressContextMenu =
                                    (value === undefined ||
                                        cell.getIsPlaceholder()) &&
                                    !cell.getIsAggregated() &&
                                    !cell.getIsGrouped();
                                const allowInteractions = suppressContextMenu
                                    ? undefined
                                    : !!value?.formatted;

                                const cellWidth = meta?.width;

                                const TableCellComponent = isRowTotal
                                    ? Table.CellHead
                                    : Table.Cell;
                                return (
                                    <TableCellComponent
                                        key={`value-${rowIndex}-${colIndex}-${data.pivotConfig.metricsAsRows}`}
                                        isMinimal={isMinimal}
                                        withAlignRight={isNumericItem(item)}
                                        w={cellWidth}
                                        miw={cellWidth}
                                        maw={cellWidth}
                                        withColor={conditionalFormatting?.color}
                                        withBoldFont={meta?.type === 'label'}
                                        withBackground={
                                            conditionalFormatting?.backgroundColor
                                        }
                                        withTooltip={
                                            labelFieldDescription ||
                                            conditionalFormatting?.tooltipContent
                                        }
                                        withInteractions={allowInteractions}
                                        withValue={value?.formatted}
                                        withMenu={(
                                            {
                                                isOpen,
                                                onClose,
                                                onCopy,
                                            }: MenuCallbackProps,
                                            render: RenderCallback,
                                        ) => (
                                            <ValueCellMenu
                                                opened={isOpen}
                                                rowIndex={rowIndex}
                                                colIndex={colIndex}
                                                item={item}
                                                value={value}
                                                getUnderlyingFieldValues={
                                                    isRowTotal
                                                        ? undefined
                                                        : getUnderlyingFieldValues
                                                }
                                                onClose={onClose}
                                                onCopy={onCopy}
                                                isMinimal={isMinimal}
                                            >
                                                {render()}
                                            </ValueCellMenu>
                                        )}
                                    >
                                        {cell.getIsGrouped() ? (
                                            <Group spacing="two" noWrap>
                                                <Button
                                                    compact
                                                    size="xs"
                                                    variant="subtle"
                                                    styles={(theme) => ({
                                                        root: {
                                                            height: 'unset',
                                                            paddingLeft:
                                                                theme.spacing
                                                                    .two,
                                                            paddingRight:
                                                                theme.spacing
                                                                    .xxs,
                                                            fontFeatureSettings:
                                                                "'tnum'",
                                                        },
                                                        leftIcon: {
                                                            marginRight: 0,
                                                        },
                                                    })}
                                                    onClick={(
                                                        e: React.MouseEvent<HTMLButtonElement>,
                                                    ) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        toggleExpander();
                                                    }}
                                                    leftIcon={
                                                        <MantineIcon
                                                            size={14}
                                                            icon={
                                                                row.getIsExpanded()
                                                                    ? IconChevronDown
                                                                    : IconChevronRight
                                                            }
                                                        />
                                                    }
                                                    style={{
                                                        color:
                                                            fontColor ??
                                                            'inherit',
                                                    }}
                                                >
                                                    ({countSubRows(row)})
                                                </Button>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </Group>
                                        ) : cell.getIsAggregated() ? (
                                            flexRender(
                                                cell.column.columnDef
                                                    .aggregatedCell ??
                                                    cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )
                                        ) : cell.getIsPlaceholder() ? null : (
                                            flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )
                                        )}
                                    </TableCellComponent>
                                );
                            })}
                        </Table.Row>
                    );
                })}

                {paddingBottom > 0 && (
                    <VirtualizedArea
                        cellCount={cellsCountWithRowNumber}
                        height={paddingBottom}
                    />
                )}
            </Table.Body>

            {hasColumnTotals ? (
                <Table.Footer withSticky>
                    {data.columnTotals?.map((row, totalRowIndex) => {
                        const totalRowCount = data.columnTotals?.length ?? 1;
                        const stickyIndex = totalRowCount - 1 - totalRowIndex;
                        return (
                            <Table.Row
                                key={`column-total-${totalRowIndex}`}
                                index={stickyIndex}
                            >
                                {/* shows empty cell if row numbers are visible */}
                                {hideRowNumbers ? null : <Table.Cell />}

                                {/* render the total label */}
                                {data.columnTotalFields?.[totalRowIndex].map(
                                    (totalLabel, totalColIndex) =>
                                        totalLabel ? (
                                            <Table.CellHead
                                                key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                                isMinimal={isMinimal}
                                                withAlignRight
                                                withBoldFont
                                            >
                                                {totalLabel.fieldId
                                                    ? `Total ${getFieldLabel(
                                                          totalLabel.fieldId,
                                                      )}`
                                                    : `Total`}
                                            </Table.CellHead>
                                        ) : (
                                            <Table.Cell
                                                key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                                isMinimal={isMinimal}
                                            />
                                        ),
                                )}

                                {row.map((total, totalColIndex) => {
                                    const value = data.pivotConfig.metricsAsRows
                                        ? getMetricAsRowColumnTotalValueFromAxis(
                                              total,
                                              totalRowIndex,
                                          )
                                        : getColumnTotalValueFromAxis(
                                              total,
                                              totalColIndex,
                                          );
                                    return value ? (
                                        <Table.CellHead
                                            key={`column-total-${totalRowIndex}-${totalColIndex}`}
                                            withAlignRight
                                            isMinimal={isMinimal}
                                            withBoldFont
                                            withInteractions
                                            withValue={value.formatted}
                                            withMenu={(
                                                {
                                                    isOpen,
                                                    onClose,
                                                    onCopy,
                                                }: MenuCallbackProps,
                                                render: RenderCallback,
                                            ) => (
                                                <TotalCellMenu
                                                    opened={isOpen}
                                                    onClose={onClose}
                                                    onCopy={onCopy}
                                                >
                                                    {render()}
                                                </TotalCellMenu>
                                            )}
                                        >
                                            {value.formatted}
                                        </Table.CellHead>
                                    ) : (
                                        <Table.Cell
                                            key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                            isMinimal={isMinimal}
                                        />
                                    );
                                })}

                                {hasRowTotals
                                    ? data.rowTotalFields?.[0].map(
                                          (_, index) => (
                                              <Table.Cell
                                                  key={`footer-empty-${totalRowIndex}-${index}`}
                                                  isMinimal={isMinimal}
                                              />
                                          ),
                                      )
                                    : null}
                            </Table.Row>
                        );
                    })}
                </Table.Footer>
            ) : null}
        </Table>
    );
};

export default PivotTable;
